import { useContext, useEffect, useState } from 'react';
import { DocumentReference } from 'firebase/firestore';
import { Drawer, Grid, Theme, Tooltip, useTheme } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { MetadataError } from '@ep/error-handling';
import BuildIcon from '@mui/icons-material/Build';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import { NavigationLink } from '@esg/esg-global-types';
import CompanySelect from '../../shared/input/select/CompanySelect';
import { ReactComponent as EditIcon } from '../../../assets/icons/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/icons/Delete.svg';
import { CompanyExtended } from '../../../lib/app/company';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import ConfigAddWidget, { CreateInput } from '../ConfigAddWidget';
import ConfigEditWidget, { EditInput } from '../ConfigEditWidget';
import { auth } from '../../../lib/google/firebase';
import DeleteConfirmModal from '../../shared/modal/DeleteConfirmModal';
import PrimaryTabs from '../../shared/tab/PrimaryTabs';
import PrimaryTab from '../../shared/tab/PrimaryTab';
import {
  NavigationLinkData,
  NavigationLinkExtended,
  createNavigationLink,
  deleteNavigationLink,
  getNavigationLinks,
  navigation_link_label,
  updateNavigationLink
} from '../../../lib/app/navigation_link';
import DataGridLoading from '../../shared/datagrid/DataGridLoading';
import DataGridNoRows from '../../shared/datagrid/DataGridNoRows';
import DataGridToolbar from '../../shared/datagrid/DataGridToolbar';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

const SOURCE = 'PanelTridiumLinks.tsx';

/**
 * Datagrid handling the display of configured company level Tridium Links.
 * @returns {JSX.Element}
 */
const PanelTridiumLinks = () => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [tab, setTab] = useState<number>(0);
  const [company, setCompany] = useState<CompanyExtended | null>(null);
  const [tridiumLinkRows, setTridiumLinkRows] = useState<
    Array<NavigationLink | NavigationLinkExtended>
  >([]);
  const [actionRow, setActionRow] = useState<NavigationLink | NavigationLinkExtended | null>(null);
  const [isCreateWidget, setIsCreateWidget] = useState<boolean>(true);
  const [displayWidgetPanelRight, setDisplayWidgetPanelRight] = useState<boolean>(false);
  const [openDialogDelete, setOpenDialogDelete] = useState<boolean>(false);
  const [gridLoading, setGridLoading] = useState<boolean>(false);
  const theme: Theme = useTheme();

  const companyInputFactory = (
    handleInputChange: (
      input_id: string,
      value: CompanyExtended | Array<CompanyExtended> | null
    ) => void
  ): JSX.Element => {
    return (
      <CompanySelect
        handleChangeCompanies={(company: Array<CompanyExtended> | CompanyExtended | null) => {
          if (!Array.isArray(company)) handleInputChange('input_company', company);
        }}
        group_by="group.name"
      />
    );
  };

  // Handler functions
  const handleCreateClick = (): void => {
    setActionRow(null);
    setIsCreateWidget(true);
    setDisplayWidgetPanelRight(true);
  };

  const handleEditClick = (row: NavigationLink): void => {
    setActionRow(row);
    setIsCreateWidget(false);
    setDisplayWidgetPanelRight(true);
  };

  const handleCloseDeleteModal = (): void => {
    setOpenDialogDelete(false);
  };

  const handleDeleteClick = (row: NavigationLink): void => {
    try {
      setActionRow(row);
      setOpenDialogDelete(true);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error
            ? err.message
            : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'handleDeleteClick'),
          {
            row: row
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to delete Tridium Link. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const handleChangeCompany = (new_company: CompanyExtended) => {
    setCompany(new_company);
  };

  const handleUpdateRows = (rows_data: Array<NavigationLink>): void => {
    setTridiumLinkRows(rows_data);
  };

  const handleChangeTab = (new_tab: number) => {
    setCompany(null);
    setTab(new_tab);
  };

  // Row functions
  const fetchRows = async (): Promise<void> => {
    setGridLoading(true);
    try {
      // Load rows from database.
      const tridium_links: Array<NavigationLink> = await getNavigationLinks(
        company?.group.id,
        company?.id
      );
      // Load rows to memory.
      setTridiumLinkRows(tridium_links);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchRows'),
          {},
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to fetch Tridium links. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
      return;
    } finally {
      setGridLoading(false);
    }
  };

  const createRow = async (
    input_company: CompanyExtended,
    name: string,
    url: string
  ): Promise<void> => {
    try {
      const tridium_link_data: NavigationLinkData = {
        name: name,
        url: url,
        type: 'tridium',
        metadata: {},
        deleted: null
      };
      const new_tridium_link: DocumentReference = await createNavigationLink(
        input_company.group.id,
        input_company.id,
        tridium_link_data,
        auth.currentUser?.email ?? ''
      );
      if (new_tridium_link) {
        handleUpdateRows([
          {
            id: new_tridium_link.id,
            ...tridium_link_data,
            company: input_company
          },
          ...tridiumLinkRows
        ]);
      }
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'createRow'),
          {
            company: input_company,
            name: name,
            url: url
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to create Tridium Link. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const updateRow = async (
    navigation_link_company: CompanyExtended,
    updated_row: NavigationLink,
    original_data: NavigationLink
  ): Promise<void> => {
    try {
      const updated_navigation_link_data: NavigationLinkData = {
        name: updated_row.name,
        deleted: updated_row.deleted,
        url: updated_row.url,
        type: updated_row.type,
        metadata: updated_row.metadata
      };
      await updateNavigationLink(
        navigation_link_company.group.id,
        navigation_link_company.id,
        updated_row.id,
        updated_navigation_link_data,
        original_data,
        auth.currentUser?.email ?? ''
      ).then(() => {
        handleUpdateRows(
          tridiumLinkRows.map((row: NavigationLink) =>
            updated_row.id === row.id ? { ...updated_row } : row
          )
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'updateRow'),
          {
            updated_row: updated_row,
            original_data: original_data
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to update Tridium Link. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
  };

  const deleteRow = async (
    navigation_link_company: CompanyExtended,
    navigation_link: NavigationLink
  ): Promise<void> => {
    try {
      await deleteNavigationLink(
        navigation_link_company.group.id,
        navigation_link_company.id,
        navigation_link.id,
        auth.currentUser?.email ?? ''
      ).then(() => {
        setTridiumLinkRows(
          tridiumLinkRows.filter((row: NavigationLink) => {
            return row.id !== navigation_link.id;
          })
        );
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'deleteRow'),
          {
            navigation_link: navigation_link
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: `Unable to remove Tridium Link. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    } finally {
      setOpenDialogDelete(false);
    }
  };

  useEffect(() => {
    try {
      (async () => {
        setTridiumLinkRows([]);
        await fetchRows();
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE),
          null,
          tracking_id
        )
      );
      setFeedbackData({
        message: `An error occurred. Tracking ID: ${tracking_id}`,
        state: true,
        type: 'error'
      });
    }
    return;
  }, [company]);

  const columns: Array<GridColDef> = [
    ...(tab === 1
      ? [
          {
            field: 'company',
            headerName: 'Company',
            valueGetter: (params: GridValueGetterParams) => {
              return params.row.company ? params.row.company.name : '';
            },
            flex: 1
          }
        ]
      : []),
    {
      field: 'name',
      headerName: 'Name',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'url',
      headerName: 'Tridium URL',
      headerAlign: 'left',
      align: 'left',
      flex: 1
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      headerAlign: 'right',
      align: 'right',
      hideable: false,
      flex: 1,
      getActions: ({ row }) => {
        return [
          <>
            <GridActionsCellItem
              key={1}
              icon={
                <Tooltip title="Edit Tridium Link">
                  <EditIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Edit"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleEditClick(row)}
              disabled={row.locked}
            />
            <GridActionsCellItem
              key={2}
              icon={
                <Tooltip title="Delete Tridium Link">
                  <DeleteIcon width={25} fill={theme.palette.primary.main} />
                </Tooltip>
              }
              size="large"
              label="Delete"
              sx={{
                color: 'primary.main'
              }}
              onClick={() => handleDeleteClick(row)}
            />
          </>
        ];
      }
    }
  ];

  const create_inputs: readonly CreateInput[] = company
    ? [
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        },
        {
          id: 'url',
          type: 'text',
          label: 'Tridium URL',
          ignoreBannedCharacters: true
        }
      ]
    : [
        {
          id: 'name',
          type: 'text',
          label: 'Name'
        },
        {
          id: 'url',
          type: 'text',
          label: 'Tridium URL',
          ignoreBannedCharacters: true
        },
        {
          id: 'input_company',
          type: 'node',
          label: 'Company',
          inputNodeFactory: companyInputFactory
        }
      ];

  const edit_inputs: EditInput[] = [
    {
      id: 'name',
      type: 'text',
      label: 'Name'
    },
    {
      id: 'url',
      type: 'text',
      label: 'Tridium URL',
      ignoreBannedCharacters: true
    }
  ];

  return (
    <>
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        open={openDialogDelete}
        allow_delete={true}
        handleCloseDeleteModal={handleCloseDeleteModal}
        delete_label={actionRow && actionRow.name}
        handleDelete={
          actionRow &&
          (async () => {
            const navigation_link_company: CompanyExtended | null | undefined =
              company ?? ('company' in actionRow ? actionRow.company : undefined);
            if (navigation_link_company) {
              await deleteRow(navigation_link_company, actionRow);
            }
          })
        }
        allow_archive={false}
      />

      {/* Side Widget Panel */}
      <Drawer
        anchor={'right'}
        open={displayWidgetPanelRight}
        onClose={() => setDisplayWidgetPanelRight(false)}
        PaperProps={{ style: { width: '30%', padding: '1.5rem' } }}
      >
        {/* Create / Edit Widget */}
        {isCreateWidget ? (
          <>
            <ConfigAddWidget
              widget_label={navigation_link_label.one}
              icon={<BuildIcon color="primary" sx={{ marginRight: '2rem' }} fontSize="large" />}
              inputs={create_inputs}
              handleClose={() => setDisplayWidgetPanelRight(false)}
              createFunction={async (name: string, url: string, input_company: CompanyExtended) => {
                const navigation_link_company: CompanyExtended | null = input_company ?? company;
                navigation_link_company && (await createRow(navigation_link_company, name, url));
              }}
            />
          </>
        ) : (
          <ConfigEditWidget
            widget_label={navigation_link_label.one}
            icon={<BuildIcon color="primary" sx={{ marginRight: '2rem' }} fontSize="large" />}
            handleClose={() => setDisplayWidgetPanelRight(false)}
            edit_entity={actionRow}
            inputs={edit_inputs}
            handleEditInput={(input_id: string, value: unknown) =>
              actionRow && setActionRow({ ...actionRow, [input_id]: value })
            }
            confirmEditFunction={async (
              updated_navigation_link: NavigationLink | NavigationLinkExtended,
              original_navigation_link: NavigationLink | NavigationLinkExtended
            ) => {
              const navigation_link_company: CompanyExtended | null | undefined =
                company ??
                ('company' in updated_navigation_link
                  ? updated_navigation_link.company
                  : undefined);
              if (navigation_link_company) {
                await updateRow(
                  navigation_link_company,
                  updated_navigation_link,
                  original_navigation_link
                );
              }
            }}
          />
        )}
      </Drawer>
      <Grid container spacing={8}>
        <Grid size={12}>
          <PrimaryTabs
            value={tab}
            onChange={(event, new_tab: number) => handleChangeTab(new_tab)}
            sx={{ width: '50%' }}
          >
            <PrimaryTab
              icon={<ApartmentOutlinedIcon />}
              iconPosition="start"
              label="Company View"
            />
            <PrimaryTab
              icon={<AutoAwesomeMosaicOutlinedIcon />}
              iconPosition="start"
              label="All Tridium Links"
            />
          </PrimaryTabs>
        </Grid>
        {tab === 0 && (
          <Grid size={6}>
            <CompanySelect
              group_by="group.name"
              handleChangeCompanies={(company: CompanyExtended | Array<CompanyExtended> | null) => {
                company && !Array.isArray(company) && handleChangeCompany(company);
              }}
            />
          </Grid>
        )}
        {((tab === 0 && company) || tab === 1) && (
          <Grid size={12}>
            {/* Interactive Data Table */}
            <DataGrid
              autoHeight
              rowHeight={75}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 10, page: 0 }
                }
              }}
              hideFooter={tridiumLinkRows.length > 1 ? false : true}
              columnHeaderHeight={tridiumLinkRows.length < 1 ? 0 : 56}
              pageSizeOptions={[10, 25, 50, 100]}
              loading={gridLoading}
              rows={tridiumLinkRows}
              columns={columns}
              disableRowSelectionOnClick
              slots={{
                toolbar: () => (
                  <DataGridToolbar
                    entity_label={navigation_link_label}
                    disable_export={tridiumLinkRows.length === 0}
                    handleCreate={handleCreateClick}
                  />
                ),
                noRowsOverlay: () => <DataGridNoRows missing_label={navigation_link_label} />,
                loadingOverlay: () => <DataGridLoading columns={columns.length} />
              }}
              slotProps={{ toolbar: { handleCreate: handleCreateClick } }}
              sx={{ '&, [class^=MuiDataGrid]': { border: 'none' }, width: '100%' }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default PanelTridiumLinks;
